import { getMarketTypeDetails } from "@/SX/helper/marketTypes"
import logger from "@/utils/logger"
import { GameState, GraphBetStatus } from "@azuro-org/toolkit"

function sxOddsToDecimal(oddsStr: string) {
  const implied = Number(oddsStr) / 1e20
  return implied === 0 ? 0 : 1 / implied
}

function sxAmountToNumber(amountStr: string) {
  return Number(amountStr) // already parsed as float by API
}

// function sxStatusToGraphBetStatus(status: string): GraphBetStatus {
//   switch (status) {
//     case "SUCCESS":
//       return GraphBetStatus.Resolved
//     case "FAILED":
//       return GraphBetStatus.Resolved
//     case "PENDING":
//       return GraphBetStatus.Accepted
//     case "CANCELLED":
//       return GraphBetStatus.Canceled
//     default:
//       return GraphBetStatus.Accepted
//   }
// }

// Helper function to get sport name from market data (keeping for fallbac

// Helper function to get sport slug from sport name
function getSportSlug(sportName: string): string {
  return sportName.toLowerCase().replace(/\s+/g, "-")
}

export function mapSxBetTradeToBet(trade: any) {
  // Handle both production and development API responses
  // In dev environment, trade includes grouped data properties
  const oddsDecimal = sxOddsToDecimal(trade?.weightedAverageOdds)
  const amount = sxAmountToNumber(trade?.totalStake)
  const payout = Number(trade?.netReturn || 0)
  const netReturn = Number(trade?.netReturn || 0)
  const isSettled = !!trade?.settled

  // For unsettleconsole.log("trade", trade.tradeStatus)d bets, set isWin/isLose to null to show as accepted
  const isWin = isSettled ? netReturn > 0 : null
  const isLose = isSettled ? netReturn <= 0 : null
  logger.log("SX.bet trade object:", {
    trade,
    marketType: trade?.marketType,
    market: trade?.market,
    hasMarket: !!trade?.market,
    marketKeys: trade?.market ? Object.keys(trade.market) : [],
  })
  let status: GraphBetStatus
  if (trade?.tradeStatus === "CANCELLED") {
    status = GraphBetStatus.Canceled
  } else if (isSettled) {
    status = GraphBetStatus.Resolved
  } else if (trade?.tradeStatus === "PENDING") {
    status = GraphBetStatus.Accepted
  } else if (trade?.tradeStatus === "SUCCESS" && trade?.settled) {
    status = GraphBetStatus.Resolved
  } else {
    status = GraphBetStatus.Accepted // Default to Accepted for unsettled bets
  }

  console.log("SX.bet status debug:", {
    tradeStatus: trade?.tradeStatus,
    isSettled,
    netReturn,
    isWin,
    isLose,
    mappedStatus: status,
    settled: trade?.settled,
  })

  const betTime = Number(trade?.betTime) * 1000
  const gameTime = new Date(trade?.gameTime).getTime()
  const isLive = betTime > gameTime

  // Get sport information from market data (same method as SXMarkets)
  const sportName = trade?.market?.sport?.label || trade?.sportName || "Tennis"
  const sportSlug = getSportSlug(sportName)
  const sportId = trade?.market?.sport?.sportId || trade?.sportId || ""

  // Get league information from market data (same method as SXMarkets)
  const leagueName = trade?.market?.league?.label || trade?.leagueLabel || ""
  const leagueId = trade?.market?.league?.leagueId || ""

  // Get participants from market data (same method as SXMarkets)
  let participants = [
    {
      name:
        trade?.market?.teamOneName ||
        trade?.gameLabel?.split(" vs ")[0] ||
        "Player A",
      image: "",
    },
    {
      name:
        trade?.market?.teamTwoName ||
        trade?.gameLabel?.split(" vs ")[1] ||
        "Player B",
      image: "",
    },
  ]

  return {
    tokenId: trade?.fillHash || trade?.fillOrderHash,
    txHash: trade?.crosschainPlaceBetTxHash || trade?.fillHash,
    createdAt: trade?.betTime || 0,
    amount: String(amount),
    payout: payout,
    totalOdds: oddsDecimal,
    isWin: isSettled ? netReturn > 0 : false, // Convert null to false for main bet object
    isLose: isSettled ? netReturn <= 0 : false, // Convert null to false for main bet object
    isCanceled: trade?.tradeStatus === "CANCELLED",
    status: status,
    possibleWin: payout,
    freebetId: null,
    isRedeemed: isSettled,
    lpAddress: trade?.bettor || "",
    coreAddress: "0x0000000000000000000000000000000000000000" as `0x${string}`,
    isRedeemable: trade.settled && netReturn > 0,
    actor: trade?.bettor || "0x0000000000000000000000000000000000000000",
    affiliate:
      trade?.benefactor || "0x0000000000000000000000000000000000000000",
    redeemedTxHash: null,
    resolvedAt: trade?.settleDate || null,
    isCashedOut: false,
    outcomes: [
      {
        odds: oddsDecimal,
        marketName: (() => {
          try {
            if (trade?.market?.type && typeof trade.market.type === "number") {
              return getMarketTypeDetails(trade.market.type).betGroup
            }
            return trade?.market?.type
          } catch (error) {
            return trade?.market?.type
          }
        })(),
        selectionName: trade?.bettingOutcomeLabel || "",
        isWin: isSettled ? netReturn > 0 : null, // Keep null for outcomes to show time
        isLose: isSettled ? netReturn <= 0 : null, // Keep null for outcomes to show time
        isLive,
        isCanceled: trade?.tradeStatus === "CANCELLED",
        outcomeId: String(trade?.bettingOutcome),
        conditionId: trade?.sportXeventId || "",
        wonOutcomeIds: isWin ? [String(trade?.outcome)] : [],
        game: {
          gameId: trade?.sportXeventId || "",
          id: trade?.sportXeventId || "",
          slug: (trade?.market?.gameLabel || trade?.gameLabel || "")
            .toLowerCase()
            .replace(/\s+/g, "-"),
          title: trade?.market?.gameLabel || trade?.gameLabel || "",
          startsAt: String(
            Math.floor(new Date(trade?.gameTime).getTime() / 1000)
          ),
          state: (() => {
            if (trade?.tradeStatus === "CANCELLED") {
              return GameState.Stopped
            }
            if (isSettled) {
              return GameState.Finished
            }
            if (trade?.tradeStatus === "PENDING") {
              return GameState.Prematch
            }
            return GameState.Prematch
          })(),
          turnover: "0",
          sport: {
            name: sportName,
            slug: sportSlug,
            sportId: String(sportId),
            sporthub: {
              __typename: "SportHub" as const,
              id: String(sportId),
              slug: sportSlug,
            },
          },
          league: {
            id: String(leagueId),
            name: leagueName,
            slug: leagueName.toLowerCase().replace(/\s+/g, "-"),
          },
          country: {
            id: "international-tournaments",
            name: "International Tournaments",
            slug: "/international-tournaments",
          },
          participants,
        },
      },
    ],
    isFreebetAmountReturnable: false,
    paymaster: null,
    baseToken: trade?.baseToken,
  }
}
